import styled, { keyframes } from 'styled-components';

export const Base = styled.div`
	height: 100vh; /* fallback for older browsers */
	height: 100svh; /* small viewport height - excludes dynamic UI */
	/* Fill available inline size inside the main content area */
	width: -webkit-fill-available;
	width: -moz-available;
	width: stretch;
	position: absolute;
	background: ${(p) => p.theme.gray[0]};
	background: linear-gradient(
		135deg,
		${(p) => p.theme.gray[0]} 0%,
		${(p) => p.theme.gray[100]} 50%,
		${(p) => p.theme.gray[0]} 100%
	);
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;

export const SidebarToggle = styled.div`
	margin-left: 10px;
	margin-top: 10px;
`;

//------------------------------------------------------------------------------------------------
// Page scaffold. The column is top-anchored (not vertically centred) so
// toggling creator modes can only grow content downward — the selector, title
// and composer never move. The title slot reserves two lines for the same
// reason.
//------------------------------------------------------------------------------------------------

export const Page = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

// The offset puts the midpoint of the shared stack (selector, title, composer,
// chips — roughly 390px tall) at the viewport's vertical center. Project mode
// is exactly that stack, so it reads centred at any window height; the
// design-system extras hang below the midpoint by design, and because the
// anchor is the top edge, toggling modes never moves the shared elements.
export const Column = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	max-width: 600px;
	padding: 0 20px 100px 20px;
	margin-top: clamp(56px, calc(50vh - 195px), 9999px);
	text-align: center;
`;

export const TitleSlot = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: center;
	min-height: 66px;
`;

export const Title = styled.div`
	font-size: 24px;
	font-weight: 500;
	color: ${(p) => p.theme.gray[1000]};
	margin-bottom: 28px;
	line-height: 1.35;
`;

//------------------------------------------------------------------------------------------------
// Mode selector
//------------------------------------------------------------------------------------------------

export const SelectorAbove = styled.div`
	margin-bottom: 16px;
`;

export const Segmented = styled.div`
	display: inline-flex;
	gap: 2px;
	padding: 3px;
	border-radius: 9px;
	background: ${(p) => p.theme.gray[200]};
	flex-shrink: 0;
`;

export const SegmentedTab = styled.button<{ $isActive: boolean }>`
	appearance: none;
	border: none;
	font-family: inherit;
	white-space: nowrap;
	transition: background 0.15s ease, color 0.15s ease;
	font-size: 13px;
	font-weight: 500;
	padding: 7px 16px;
	border-radius: 7px;
	color: ${(p) => (p.$isActive ? p.theme.gray[1000] : p.theme.gray[600])};
	background: ${(p) => (p.$isActive ? p.theme.gray[0] : 'transparent')};
	box-shadow: ${(p) => (p.$isActive ? '0 1px 3px rgba(0, 0, 0, 0.12)' : 'none')};

	&:hover {
		color: ${(p) => p.theme.gray[1000]};
	}
`;

//------------------------------------------------------------------------------------------------
// Starter kit teaser — opens the showcase modal, which carries the full
// descriptions, tags and sample-project links
//------------------------------------------------------------------------------------------------

const riseIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(5px);
	}
	to {
		opacity: 1;
		transform: none;
	}
`;

// Arrives after the chips have settled, so switching into design-system mode
// reads as one calm sequence instead of everything appearing at once
export const Teaser = styled.button`
	display: flex;
	align-items: center;
	gap: 14px;
	width: 100%;
	margin-top: 36px;
	padding: 12px 14px;
	border: 1px solid ${(p) => p.theme.gray[200]};
	border-radius: 12px;
	background: ${(p) => p.theme.gray[0]};
	font-family: inherit;
	text-align: left;
	animation: ${riseIn} 0.26s ease 0.14s both;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;

	&:hover {
		border-color: ${(p) => p.theme.gray[400]};
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
	}
`;

export const TeaserThumbs = styled.div`
	display: flex;
	flex-shrink: 0;
	gap: 6px;
`;

export const ThumbFrame = styled.span`
	display: block;
	overflow: hidden;
	width: 54px;
	height: 38px;
	border-radius: 5px;
	border: 1px solid ${(p) => p.theme.gray[200]};
`;

// Shows the top-left corner of the screen at ~2×: at thumbnail size a whole
// dense page turns to noise, but a corner still reads as real UI
export const ThumbImg = styled.img`
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: top left;
	transform: scale(1.9);
	transform-origin: top left;
`;

export const TeaserCopy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	flex: 1;
`;

export const TeaserTitle = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: ${(p) => p.theme.gray[1000]};
`;

export const TeaserDescription = styled.div`
	font-size: 11.5px;
	line-height: 1.4;
	color: ${(p) => p.theme.gray[600]};
`;

export const TeaserArrow = styled.div`
	display: flex;
	align-items: center;
	flex-shrink: 0;
`;
